import { clsx } from "clsx";
import { type FC, type ReactElement, useEffect, useRef } from "react";
import { Card, CardRowDetail, CardRowDetailColumn, CardRowDetailLine } from "widgets";

import { getHspHitRange, getHspName } from "@/app-utils/blast.ts";
import { getBitScoreFillColor } from "@/app-utils/color.ts";
import { FeatureType, getColorScheme } from "@/app-utils/genome-browser-color.ts";
import { type IPileupHsp, type IPileupParams } from "@/app-utils/track-shapes/track-pileup-factory.ts";
import { type BlastHit, type BlastHsp } from "@/graphql.ts";
import { MIN_SHAPE_WIDTH, STRAND_TYPE } from "@/settings/settings.js";

import { TRACK_TOOLTIP_TYPE } from "../../app-utils/track-shapes/shape-feature.js";
import { renderArrowShape } from "../genome-viewer/ArrowShape.js";
import { type SvgCallTooltip } from "../genome-viewer/BlastHitTrack.js";
import { SelectRangeButton } from "../genome-viewer/SelectRangeButton.js";

const hspShapePileupParams: IPileupParams = {
  featureHeight: 8,
  lanePadding: 7,
  labelHeight: 12,
  minShapeWidth: MIN_SHAPE_WIDTH,
  charWidthInPixel: 6,
  maxLanes: 30,
};

export interface IHspShapeProps {
  pileup: IPileupHsp;
  callTooltip: SvgCallTooltip;
}

export interface IBlastHspTooltipData {
  pileupHsp: IPileupHsp;
}

/**
 * A visualization component that draws the HSP shapes of a BLAST hit, possibly containing several HSPs.
 *
 * The component is expected to be used within an existing SVG context and does NOT create its own SVG element. The
 * main input data is a pileup hit whose view coordinates and a width have already been computed elsewhere.
 *
 */
const HspShape: FC<IHspShapeProps> = (props) => {
  const hspShapeRef = useRef(undefined);
  const pileupHsp: IPileupHsp = props.pileup;
  const hit: BlastHit = pileupHsp.hit;
  const hsp: BlastHsp = pileupHsp.hsp;
  const height: number = hspShapePileupParams.featureHeight;

  const hspName: string = getHspName(hit, hsp);
  const labelFits: boolean = pileupHsp.width > hspName.length * hspShapePileupParams.charWidthInPixel;
  const arrowFits: boolean = pileupHsp.width > height;
  const translationFrame: number = Number.parseInt(hsp.frame, 10);
  const isForwardStrand: boolean =
    pileupHsp.hitStrand === STRAND_TYPE.PLUS && (isNaN(translationFrame) || translationFrame >= 0);
  const translateHsp = `translate(${pileupHsp.x}, ${pileupHsp.y})`;
  const score: number = hsp.score;
  const colorSet = getColorScheme(FeatureType.BLAST, getBitScoreFillColor(score)).colorScheme;
  const shapeStyle = {
    fill: isForwardStrand ? colorSet.medium : colorSet.light,
    stroke: isForwardStrand ? colorSet.medium : colorSet.dark,
  };
  const textStyle = { fill: colorSet.darker };
  const textClasses: string = clsx("feature-name", {
    "label-top-middle": labelFits,
    "label-top": !labelFits,
  });

  const shapeClasses: string = clsx("feature-shape", {
    "feature-plus-strand": isForwardStrand,
    "feature-minus-strand": !isForwardStrand,
  });

  const translateFeature = `translate(0, 0)`;
  const groupClasses = clsx("feature");

  const hideLabel = false;
  const shape: ReactElement = renderArrowShape(
    hspName,
    `hit-shape-${hit.rank}-${pileupHsp.hsp.rank}`,
    height,
    pileupHsp.width,
    -height,
    "0.25em",
    pileupHsp.startCut,
    pileupHsp.endCut,
    isForwardStrand,
    arrowFits,
    labelFits,
    hideLabel,
    shapeClasses,
    shapeStyle,
    textClasses,
    textStyle,
    groupClasses,
    translateFeature,
    null,
  );

  useEffect(() => {
    props.callTooltip(hspShapeRef.current, hspShapeRef.current, {
      uid: `hsp-${pileupHsp.hsp.id}`,
      title: `Hit ${hit.rank} HSP ${pileupHsp.hsp.rank}`,
      type: TRACK_TOOLTIP_TYPE.BLAST_HIT,
      content: {
        pileupHsp: pileupHsp,
      } as IBlastHspTooltipData,
    });
  }, [props.callTooltip]);

  return (
    <g ref={hspShapeRef} id={`hit-shape-${hit.rank}-${pileupHsp.hsp.rank}`} className="hit" transform={translateHsp}>
      {shape}
    </g>
  );
};

const TrackBlastHspTooltip: FC<IBlastHspTooltipData> = ({ pileupHsp }) => {
  return (
    <Card
      title={`Hit ${pileupHsp.hit.rank} HSP ${pileupHsp.hsp.rank}`}
      subtitle={"Blast HSP"}
      actions={[<SelectRangeButton range={getHspHitRange(pileupHsp.hsp)} />]}
      noBorder
    >
      <CardRowDetail>
        <CardRowDetailColumn>
          <CardRowDetailLine title={"Score"}>{pileupHsp.hsp.score} bits</CardRowDetailLine>
          <CardRowDetailLine title={"E-Value"}>{pileupHsp.hsp.eValue}</CardRowDetailLine>
        </CardRowDetailColumn>
      </CardRowDetail>
    </Card>
  );
};

export { HspShape, hspShapePileupParams, TrackBlastHspTooltip };
